import { Button } from "@studyplus/boron-ui";
import classNames from "classnames";
import { useCallback } from "react";
import { WithRouterProps, withRouter } from "../../../helpers/RouterHelper";
import {
  MessageDestinationInterface,
  MessageFormStateInterface,
} from "../../../interfaces/MessageInterface";
import OperatorInterface from "../../../interfaces/OperatorInterface";
import OperatorProfileInterface from "../../../interfaces/OperatorProfileInterface";
import { PostZoomMeetingMessageParams } from "../../../interfaces/PostZoomMeetingMessageParams";
import {
  AttachmentPreviewModal,
  useAttachmentPreview,
} from "../../features/AttachmentPreview";
import TooltipIconButton from "../TooltipIcon/index";
import { ApiErrors } from "./ApiErrors";
import { AttachmentIcon, FileType } from "./AttachmentIcon";
import { ContentField } from "./ContentField";
import styles from "./styles.scss";
import { useZoomIntegration } from "./useZoomIntegration";
import { ZoomModal } from "./ZoomModal/index";

interface ZoomIntegrationProps {
  destination?: MessageDestinationInterface | null;
  operator?: OperatorInterface;
  operatorProfile?: OperatorProfileInterface | null;
  postZoomMeetingMessage?: (
    params: PostZoomMeetingMessageParams,
    setSubmitting: (submitting: boolean) => void,
    setErrors: (errors: Record<string, unknown>) => void,
    onSuccessCallback: () => void,
  ) => void;
  defaultZoomMeetingContent?: string;
}

interface Props extends WithRouterProps {
  formState?: MessageFormStateInterface;
  canSendMessage: boolean;
  allowedFileTypes: FileType[];
  disabledPlaceholder: string;
  postMessage: (content: string) => void;
  postMessageFile: (file: File) => void;
  changeFormContent: (content: string) => void;
  changeFormFile: (file: File | null) => void;
  rows?: number;
  storageKey: string;
  zoomIntegration?: ZoomIntegrationProps;
  textareaMaxHeightBaseElementRef?: React.RefObject<HTMLElement | null>;
}

const isValid = (formState: MessageFormStateInterface): boolean => {
  const { content, file } = formState.values;

  return !!content || !!file;
};

const CommonMessageForm = ({
  formState,
  canSendMessage,
  allowedFileTypes,
  disabledPlaceholder,
  postMessage,
  postMessageFile,
  changeFormContent,
  changeFormFile,
  rows,
  storageKey,
  zoomIntegration,
  navigate,
  textareaMaxHeightBaseElementRef,
}: Props) => {
  const {
    isModalOpen,
    handleClickIcon,
    handleModalClose,
    handlePostZoomMeetingMessage,
  } = useZoomIntegration({
    operatorProfile: zoomIntegration?.operatorProfile,
    postZoomMeetingMessage: zoomIntegration?.postZoomMeetingMessage,
    navigate,
  });

  const attachmentPreview = useAttachmentPreview({
    changeFormFile,
    destinationKey: storageKey,
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!formState) {
        return;
      }

      attachmentPreview.clearFileError();
      if (formState.values.file) {
        postMessageFile(formState.values.file);
      } else {
        postMessage(formState.values.content);
      }
    },
    [formState, postMessage, postMessageFile, attachmentPreview.clearFileError],
  );

  const isSubmitDisabled =
    !formState || (formState && (formState.submitting || !isValid(formState)));
  const shouldShowZoomIcon =
    canSendMessage &&
    zoomIntegration?.operatorProfile &&
    zoomIntegration?.operator &&
    zoomIntegration?.destination &&
    formState;
  const shouldShowAttachmentIcon = canSendMessage && formState;
  const apiErrors = [
    ...(formState?.apiErrors || []),
    ...attachmentPreview.fileErrors,
  ];

  return (
    <div>
      <form className={styles.root} onSubmit={handleSubmit}>
        <ApiErrors apiErrors={apiErrors} className={styles.apiErrorsMobile} />
        <div
          className={classNames(styles.content, {
            [styles.hasError]: apiErrors.length > 0,
          })}
        >
          <ContentField
            formState={formState}
            hasError={apiErrors.length > 0}
            canSendMessage={canSendMessage}
            disabledPlaceholder={disabledPlaceholder}
            changeFormContent={changeFormContent}
            changeFormFile={changeFormFile}
            rows={rows}
            storageKey={storageKey}
            textareaMaxHeightBaseElementRef={textareaMaxHeightBaseElementRef}
          />
          {(shouldShowZoomIcon || shouldShowAttachmentIcon) && (
            <div className={styles.iconContainer}>
              {shouldShowZoomIcon && (
                <TooltipIconButton
                  id={"common-message-form-zoom-icon"}
                  iconName="icon-zoom"
                  tooltipLabel={"ZoomのミーティングURLを自動発行"}
                  onClick={handleClickIcon}
                  disabled={!!formState.values.content || formState.submitting}
                />
              )}
              {shouldShowAttachmentIcon && (
                <AttachmentIcon
                  allowedFileTypes={allowedFileTypes}
                  formState={formState}
                  onSelectFile={attachmentPreview.selectFile}
                />
              )}
            </div>
          )}
        </div>
        <div className={styles.button}>
          <Button type="submit" disabled={isSubmitDisabled} isBlock={true}>
            送信
          </Button>
        </div>
      </form>
      <ApiErrors apiErrors={apiErrors} className={styles.apiErrorsDesktop} />
      <AttachmentPreviewModal
        file={attachmentPreview.previewFile}
        onConfirm={attachmentPreview.confirm}
        onCancel={attachmentPreview.cancel}
      />
      {zoomIntegration &&
        zoomIntegration.operatorProfile &&
        zoomIntegration.operator &&
        zoomIntegration.destination &&
        zoomIntegration.defaultZoomMeetingContent && (
          <ZoomModal
            onClose={handleModalClose}
            onSubmit={handlePostZoomMeetingMessage}
            isOpened={isModalOpen}
            operator={zoomIntegration.operator}
            operatorProfile={zoomIntegration.operatorProfile}
            destination={zoomIntegration.destination}
            defaultContent={zoomIntegration.defaultZoomMeetingContent}
          />
        )}
    </div>
  );
};

export default withRouter<Props>(CommonMessageForm);
